import { captureException } from '@sentry/react';
import { ZodError } from 'zod';

import { apiClient } from '@f/api-client';

import { createAction } from '../actions';

export const BATCH_SEARCH_REQUEST_FIND_START = 'BATCH_SEARCH_REQUEST_FIND_START';
export const BATCH_SEARCH_REQUEST_FIND_FULFILL = 'BATCH_SEARCH_REQUEST_FIND_FULFILL';
export const BATCH_SEARCH_REQUEST_FIND_REJECT = 'BATCH_SEARCH_REQUEST_FIND_REJECT';

export const BATCH_SEARCH_REQUEST_GET_START = 'BATCH_SEARCH_REQUEST_GET_START';
export const BATCH_SEARCH_REQUEST_GET_FULFILL = 'BATCH_SEARCH_REQUEST_GET_FULFILL';
export const BATCH_SEARCH_REQUEST_GET_REJECT = 'BATCH_SEARCH_REQUEST_GET_REJECT';

export const BATCH_SEARCH_REQUEST_UPDATE_START = 'BATCH_SEARCH_REQUEST_UPDATE_START';
export const BATCH_SEARCH_REQUEST_UPDATE_FULFILL = 'BATCH_SEARCH_REQUEST_UPDATE_FULFILL';
export const BATCH_SEARCH_REQUEST_UPDATE_REJECT = 'BATCH_SEARCH_REQUEST_UPDATE_REJECT';

export const BATCH_SEARCH_REQUEST_REMOVE_START = 'BATCH_SEARCH_REQUEST_REMOVE_START';
export const BATCH_SEARCH_REQUEST_REMOVE_FULFILL = 'BATCH_SEARCH_REQUEST_REMOVE_FULFILL';
export const BATCH_SEARCH_REQUEST_REMOVE_REJECT = 'BATCH_SEARCH_REQUEST_REMOVE_REJECT';

export const BATCH_SEARCH_RESULT_FIND_START = 'BATCH_SEARCH_RESULT_FIND_START';
export const BATCH_SEARCH_RESULT_FIND_FULFILL = 'BATCH_SEARCH_RESULT_FIND_FULFILL';
export const BATCH_SEARCH_RESULT_FIND_REJECT = 'BATCH_SEARCH_RESULT_FIND_REJECT';

export const BATCH_SEARCH_RESULT_GET_START = 'BATCH_SEARCH_RESULT_GET_START';
export const BATCH_SEARCH_RESULT_GET_FULFILL = 'BATCH_SEARCH_RESULT_GET_FULFILL';
export const BATCH_SEARCH_RESULT_GET_REJECT = 'BATCH_SEARCH_RESULT_GET_REJECT';

export const BATCH_SEARCH_RESULT_ADD_TO_REPORT_START = 'BATCH_SEARCH_RESULT_ADD_TO_REPORT_START';
export const BATCH_SEARCH_RESULT_ADD_TO_REPORT_FULFILL = 'BATCH_SEARCH_RESULT_ADD_TO_REPORT_FULFILL';
export const BATCH_SEARCH_RESULT_ADD_TO_REPORT_REJECT = 'BATCH_SEARCH_RESULT_ADD_TO_REPORT_REJECT';

export function findRequests(query = {}, options = {}) {
  return async dispatch => {
    // Starts
    dispatch(createAction(BATCH_SEARCH_REQUEST_FIND_START));

    try {
      const route = options.findAll ? apiClient.search.adminFetchSearches : apiClient.search.fetchSearches;
      const { status, body } = await route({ query });

      if (status !== 200) {
        throw new Error(body);
      }

      // Fulfills
      dispatch(createAction(BATCH_SEARCH_REQUEST_FIND_FULFILL, { result: body }));
      return {
        total: body.pagination.total,
        limit: query.limit,
        skip: query.skip,
        ids: body.data.map(item => item.id),
      };
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      // Rejects
      dispatch(createAction(BATCH_SEARCH_REQUEST_FIND_REJECT, error));
    }
  };
}

export function getRequest(requestId, options = {}) {
  return async dispatch => {
    // Starts
    dispatch(createAction(BATCH_SEARCH_REQUEST_GET_START));

    try {
      const route = options.findAny ? apiClient.search.fetchAnyUniqueSearch : apiClient.search.fetchUniqueSearch;
      const { status, body } = await route({
        params: {
          searchId: requestId,
        },
      });

      if (status !== 200) {
        throw new Error(body);
      }

      // Fulfills
      dispatch(createAction(BATCH_SEARCH_REQUEST_GET_FULFILL, { id: requestId, result: body }));
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      // Rejects
      dispatch(createAction(BATCH_SEARCH_REQUEST_GET_REJECT, error));
    }
  };
}

export function updateRequestPriority(id, priority) {
  return async dispatch => {
    // Starts
    dispatch(createAction(BATCH_SEARCH_REQUEST_UPDATE_START));

    try {
      const { status, body } = await apiClient.search.updateSearchPriority({
        params: {
          searchId: id,
        },
        body: { priority },
      });

      if (status !== 200) {
        throw new Error(body);
      }

      // Fulfills
      dispatch(createAction(BATCH_SEARCH_REQUEST_UPDATE_FULFILL, { id, result: body }));
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      // Rejects
      dispatch(createAction(BATCH_SEARCH_REQUEST_UPDATE_REJECT, error));
    }
  };
}

export function updateRequestName(id, name) {
  return async dispatch => {
    // Starts
    dispatch(createAction(BATCH_SEARCH_REQUEST_UPDATE_START, { id, name }));

    try {
      const { status, body } = await apiClient.search.updateSearchName({
        params: {
          searchId: id,
        },
        body: { name },
      });
      if (status !== 200) {
        throw new Error(body);
      }

      // Fulfills
      dispatch(createAction(BATCH_SEARCH_REQUEST_UPDATE_FULFILL, { id, result: body }));
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      // Rejects
      dispatch(createAction(BATCH_SEARCH_REQUEST_UPDATE_REJECT, error));
    }
  };
}

export function removeRequests(requestIds) {
  return async dispatch => {
    // Starts
    dispatch(createAction(BATCH_SEARCH_REQUEST_REMOVE_START));

    try {
      const { status, body } = await apiClient.search.removeSearches({
        body: requestIds,
      });

      if (status !== 200) {
        throw new Error(body);
      }

      // Fulfills
      dispatch(createAction(BATCH_SEARCH_REQUEST_REMOVE_FULFILL, { result: body }));
      return requestIds;
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      // Rejects
      dispatch(createAction(BATCH_SEARCH_REQUEST_REMOVE_REJECT, error));
    }
  };
}

export function getResult(params, query) {
  return async dispatch => {
    // Starts
    dispatch(createAction(BATCH_SEARCH_RESULT_GET_START));

    try {
      const { status, body } = await apiClient.search.fetchUniqueSearchResult({
        params,
        query,
      });

      if (status !== 200) {
        throw new Error(body);
      }

      // Fulfills
      dispatch(createAction(BATCH_SEARCH_RESULT_GET_FULFILL, { id: body.id, result: body }));
      return body.id;
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      // Rejects
      dispatch(createAction(BATCH_SEARCH_RESULT_GET_REJECT, error));
      throw error;
    }
  };
}

export function addToReport({
  reportId,
  elements,
  searchResultIds,
  searchId,
  scifinderToken,
  includeScifinderRelatedData,
}) {
  return async dispatch => {
    dispatch(createAction(BATCH_SEARCH_RESULT_ADD_TO_REPORT_START, { elements }));

    try {
      const requestConfig = {
        headers: scifinderToken
          ? {
              authorization: `Bearer ${scifinderToken}`,
            }
          : {},
        params: {
          reportId,
        },
        body: {
          elements,
          searchResultIds,
          searchId,
          includeScifinderRelatedData,
        },
        redirectUnauthorized: false,
      };

      const { status, body } = await apiClient.report.addReportItems(requestConfig);

      if (status !== 201) {
        throw console.error(body);
      }

      dispatch(createAction(BATCH_SEARCH_RESULT_ADD_TO_REPORT_FULFILL, { ids: searchResultIds, result: body }));
      return body;
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      dispatch(createAction(BATCH_SEARCH_RESULT_ADD_TO_REPORT_REJECT, error));
      throw error;
    }
  };
}
